import { useSyncExternalStore } from 'react';

import type { AppearancePreferences } from '@app/shared';

/**
 * Preferences cached on the device, per user, in localStorage (every access
 * is guarded: storage can be unavailable in private browsing).
 *  - `appearance` mirrors the account's saved appearance (platform/appearance.ts
 *    keeps it in sync), so the look follows the person to every device.
 *  - Everything else is genuinely per device: home layout, calendar view,
 *    offline pins, the app lock credential.
 */
export interface DevicePrefs {
  appearance: AppearancePreferences & {
    /** A family photo shown behind the Today header (file id), or null. Per device. */
    headerPhotoFileId: string | null;
  };
  /** pending: a local appearance change not yet saved to the account. */
  appearanceSync: { pending: boolean; serverUpdatedAt: string | null };
  home: { order: string[]; hidden: string[] };
  calendar: { view: 'month' | 'week' | 'day' | 'agenda'; hiddenMembers: string[] };
  lists: { hideChecked: boolean };
  lastFamilyId: string | null;
  /** Records the user explicitly marked to keep available offline (by collection:id). */
  offlinePins: string[];
  /** Device app lock (WebAuthn platform authenticator credential id), or null when off. */
  security: { appLockCredentialId: string | null };
}

export const DEFAULT_PREFS: DevicePrefs = {
  appearance: { mode: 'system', theme: null, accent: null, background: null, reduceMotion: 'system', textSize: 'normal', headerPhotoFileId: null },
  appearanceSync: { pending: false, serverUpdatedAt: null },
  home: { order: [], hidden: [] },
  calendar: { view: 'month', hiddenMembers: [] },
  lists: { hideChecked: false },
  lastFamilyId: null,
  offlinePins: [],
  security: { appLockCredentialId: null },
};

const listeners = new Set<() => void>();
let currentUser = 'anonymous';
let cache: DevicePrefs = DEFAULT_PREFS;

const key = () => `app:prefs:${currentUser}`;

function load(): DevicePrefs {
  try {
    const raw = localStorage.getItem(key());
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<DevicePrefs>;
    return {
      ...DEFAULT_PREFS,
      ...parsed,
      appearance: { ...DEFAULT_PREFS.appearance, ...parsed.appearance },
      appearanceSync: { ...DEFAULT_PREFS.appearanceSync, ...parsed.appearanceSync },
      home: { ...DEFAULT_PREFS.home, ...parsed.home },
      calendar: { ...DEFAULT_PREFS.calendar, ...parsed.calendar },
      lists: { ...DEFAULT_PREFS.lists, ...parsed.lists },
      security: { ...DEFAULT_PREFS.security, ...parsed.security },
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

export function setPrefsUser(userId: string | null) {
  currentUser = userId ?? 'anonymous';
  cache = load();
  listeners.forEach((l) => l());
}

export function getPrefs(): DevicePrefs {
  return cache;
}

export function updatePrefs(update: (prev: DevicePrefs) => DevicePrefs) {
  cache = update(cache);
  try {
    localStorage.setItem(key(), JSON.stringify(cache));
  } catch {
    // Storage unavailable: keep the in-memory value for this session.
  }
  listeners.forEach((l) => l());
}

export function usePrefs(): DevicePrefs {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => cache,
  );
}

cache = load();
